'use client';

import React, { memo } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { cn } from '@/shared/lib/cn';
import type { HistoryItem } from '../model/history.types';
import { AlbumCoverView } from './AlbumCoverView';

interface AlbumCardProps {
  item: HistoryItem;
  /**
   * Whether to render the label block (title + meta line) under the cover.
   * The dashboard carousel passes `false` to show the cover only and keep the
   * home screen visually clean — parity with Apple/Android dashboard cards.
   * The History grid and project albums keep it `true`.
   */
  showLabel?: boolean;
}

/**
 * Album card for history grid — matches Android HistoryItemCard for albums.
 * Uses AlbumCoverView (teal container with paper design) instead of plain icon.
 * Info section below shows "PDF Album" title + concept display title.
 */
export const AlbumCard = memo(function AlbumCard({
  item,
  showLabel = true,
}: AlbumCardProps) {
  const t = useTranslations('history');
  const tConcept = useTranslations('concept');
  const router = useRouter();
  const attrs = item.attributes;

  const projectSequenceNumber = attrs.project_sequence_number || 0;
  const conceptTitle = attrs.name || '';
  const displayTitle =
    conceptTitle ||
    tConcept('conceptNumber', { number: projectSequenceNumber });
  const publicUrl = attrs.album_public_url || '';
  const projectId = item.relationships.project.data.id;
  const conceptId = item.conceptId;

  // Album status — default to completed for legacy items
  const albumStatus = attrs.album_status || 'completed';

  const handleClick = () => {
    if (conceptId && projectId) {
      router.push(`/project/${projectId}/concept/${conceptId}?tab=album`);
    } else if (publicUrl) {
      window.open(publicUrl, '_blank');
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'block group',
        conceptId || publicUrl
          ? 'cursor-pointer'
          : 'opacity-50 cursor-not-allowed'
      )}
    >
      {/* AlbumCoverView paints its own white paper, rounded corners and
          drop-shadow — no Card / overflow-hidden wrapper here, those would
          clip the shadow that's supposed to fall around the album. */}
      <div className="aspect-[4/3] relative transition-all hover:scale-[1.01]">
        <AlbumCoverView
          coverImageUrl={attrs.album_cover_image_url}
          conceptTitle={conceptTitle}
          conceptNumber={projectSequenceNumber}
          format={attrs.album_format || 'A4'}
          createdAt={attrs.created_at}
          status={albumStatus}
          startedAt={attrs.album_started_at}
          durationPredicted={attrs.album_duration_predicted}
          albumId={attrs.album_id}
          albumTitle={attrs.album_title ?? undefined}
        />
      </div>

      {/* Info section below card — omitted on the dashboard carousel (showLabel=false) */}
      {showLabel && (
        <div className="px-2 pt-2 pb-1">
          <p className="text-list-item-value truncate">
            {attrs.album_title || t('pdfAlbum')}
          </p>
          <p className="text-card-meta text-muted-foreground truncate">{displayTitle}</p>
        </div>
      )}
    </div>
  );
});
